import {
	useCreateProjectAdditionalTeamMemberInformationMutation,
	useUpdateProjectAdditionalTeamMemberInformationMutation,
} from "@repo/server-api";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "../../../shared/src/common/useNotifier";

import type { ServerError } from "../../../shared/src/common/useNotifier";
import type { ProjectTeamMember } from "../types/ProjectTypes";

type UseProjectAdditionalTeamMemberInfoOptions = {
	projectId: string;
	onSaved: (teamMember: ProjectTeamMember) => void;
};

type UseProjectAdditionalTeamMemberInfoResult = {
	saveProjectTeamMember: (teamMember: ProjectTeamMember) => void;
	loading: boolean;
};

type UpsertResult = {
	data: ProjectTeamMember | null;
	errors: ServerError[] | null;
	exception: unknown | null;
};

// Here is the hook
export function useSaveProjectAdditionalTeamMemberInformation(
	options: UseProjectAdditionalTeamMemberInfoOptions,
): UseProjectAdditionalTeamMemberInfoResult {
	const { t } = useTranslation();

	const { notifySuccess, notifyErrors, notifyException } = useNotifier();

	const { projectId, onSaved } = options;
	const [loading, setLoading] = useState(false);

	// Update Mutation
	const [updateProjectTeamMember] = useUpdateProjectAdditionalTeamMemberInformationMutation();

	const updateTeamMember = async (teamMember: ProjectTeamMember): Promise<UpsertResult> => {
		const result = await updateProjectTeamMember({
			variables: {
				informationId: teamMember.id!,
				projectId,
				memberId: teamMember.teamMember?.id ?? null,
				worksOnPositions: teamMember.worksOnPositions.map((w) => ({
					positionId: w.positionId,
					pricePerHour: w.pricePerHour,
					hours: w.hours,
				})),
				contractorId: teamMember.contractor?.id ?? null,
			},
		});

		// Return the updated TeamMember if successful
		const resultData = result.data?.updateProjectAdditionalTeamMemberInformation
			?.additionalTeamMemberInformation
			? teamMember
			: null;

		return {
			data: resultData,
			errors: result.data?.updateProjectAdditionalTeamMemberInformation.errors || null,
			exception: result.errors || null,
		};
	};

	// Create Mutation
	const [createProjectTeamMember] = useCreateProjectAdditionalTeamMemberInformationMutation();

	const createTeamMember = async (teamMember: ProjectTeamMember): Promise<UpsertResult> => {
		// CREATE
		const result = await createProjectTeamMember({
			variables: {
				projectId,
				memberId: teamMember.teamMember?.id ?? null,
				worksOnPositions: teamMember.worksOnPositions.map((w) => ({
					positionId: w.positionId,
					pricePerHour: w.pricePerHour,
					hours: w.hours,
				})),
				contractorId: teamMember.contractor?.id ?? null,
			},
		});

		// Return the updated TeamMember if successful, get the generated id
		const resultData = result.data?.createProjectAdditionalTeamMemberInformation
			?.additionalTeamMemberInformation
			? {
					...teamMember,
					id: result.data?.createProjectAdditionalTeamMemberInformation
						.additionalTeamMemberInformation.id,
				}
			: null;

		return {
			data: resultData,
			errors: result.data?.createProjectAdditionalTeamMemberInformation.errors || null,
			exception: result.errors || null,
		};
	};

	const saveProjectTeamMember = async (TeamMember: ProjectTeamMember) => {
		setLoading(true);
		try {
			// Update or create
			const result = TeamMember.id
				? await updateTeamMember(TeamMember)
				: await createTeamMember(TeamMember);

			if (result.errors && result.errors.length > 0) {
				notifyErrors(result.errors);
				return;
			}

			if (result.exception) {
				notifyException(result.exception);
				return;
			}

			if (!result.data) {
				notifyException(t("no-data-returned-from-upsert"));
				return;
			}

			notifySuccess(t("project-TeamMember-updated"));

			onSaved(result.data);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		saveProjectTeamMember,
		loading,
	};
}
